import React, { CSSProperties, useId } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-common-types";

type Props = {
  icon: IconDefinition;
  size?: number; // px
  angle?: number; // linear gradient angle in degrees
  colors?: string[]; // at least two colors
  stops?: number[]; // optional, same length as colors, 0..1
  className?: string;
  style?: CSSProperties;
  title?: string;
  ariaLabel?: string;
};

const DEFAULT_ANGLE = 135;
const DEFAULT_COLORS = ["#00A1E6", "#00aa44"];

function angleToVector(angleDeg: number) {
  // Convert CSS angle (deg, 0 = pointing right, increasing clockwise)
  // to SVG linearGradient vector (x1,y1 -> x2,y2) in [0,1] viewport units
  const a = ((angleDeg % 360) + 360) % 360;
  const rad = (a * Math.PI) / 180;
  const x = Math.cos(rad);
  const y = Math.sin(rad);
  // Map [-1,1] to [0,1]
  return {
    x1: 0.5 - x / 2,
    y1: 0.5 - y / 2,
    x2: 0.5 + x / 2,
    y2: 0.5 + y / 2
  };
}

/**
 * GradientFAIcon renders any Font Awesome IconDefinition with a linear SVG gradient.
 * Uses an SVG mask so single-path and duotone (multi-path) icons both work.
 */
const GradientFAIcon: React.FC<Props> = React.memo(
  ({ icon, size = 24, angle, colors, stops, className, style, title, ariaLabel }) => {
    const id = useId();
    const gradId = `gfi-grad-${id}`;
    const maskId = `gfi-mask-${id}`;

    const [w, h, , , svgPathData] = icon.icon;
    const paths = Array.isArray(svgPathData) ? svgPathData : [svgPathData];

    const gAngle = angle ?? DEFAULT_ANGLE;
    const gColors = (colors && colors.length >= 2 ? colors : DEFAULT_COLORS).slice();
    const gStops =
      stops && stops.length === gColors.length
        ? stops
        : gColors.map((_, i) => (gColors.length === 1 ? 0 : i / (gColors.length - 1)));

    return (
      <svg
        className={className}
        style={{ width: size, height: size, display: "inline-block", verticalAlign: "-0.125em", ...style }}
        viewBox={`0 0 ${w} ${h}`}
        xmlns="http://www.w3.org/2000/svg"
        role={ariaLabel ? "img" : "presentation"}
        aria-label={ariaLabel}
      >
        {title ? <title>{title}</title> : null}
        <defs>
          {(() => {
            const { x1, y1, x2, y2 } = angleToVector(gAngle);
            return (
              <linearGradient
                id={gradId}
                x1={`${x1 * 100}%`}
                y1={`${y1 * 100}%`}
                x2={`${x2 * 100}%`}
                y2={`${y2 * 100}%`}
              >
                {gStops.map((offset, i) => (
                  <stop key={i} offset={`${offset * 100}%`} stopColor={gColors[i]} />
                ))}
              </linearGradient>
            );
          })()}

          <mask id={maskId} maskUnits="userSpaceOnUse">
            {paths.map((d, i) => (
              <path key={i} d={d as string} fill="#fff" fillRule="evenodd" clipRule="evenodd" />
            ))}
          </mask>
        </defs>

        <rect width={w} height={h} fill={`url(#${gradId})`} mask={`url(#${maskId})`} />
      </svg>
    );
  }
);

export default GradientFAIcon;
